/**
 * NetworkTree — полноэкранная визуализация партнёрского дерева.
 *
 * Рендер — @xyflow/react (React Flow). Ноды разложены горизонтально:
 * root слева, L1 — рядом, L2 — дальше и т.д. до L10. Лейаут считает
 * dagre. Поддержка drag / zoom / pinch / минимапа — из коробки.
 *
 * По клику на ноду открывается карточка пользователя с деталями.
 * Сверху — поисковое поле: по @username или по telegram_id,
 * центрирует камеру на найденной ноде.
 */
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
} from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ReactFlow,
  ReactFlowProvider,
  Background,
  BackgroundVariant,
  MiniMap,
  Handle,
  Position,
  useReactFlow,
  type Node,
  type Edge,
  type NodeProps,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import dagre from 'dagre';
import { motion, AnimatePresence } from 'motion/react';
import {
  ArrowLeft,
  Search,
  X,
  Users,
  Zap,
  Trophy,
  TrendingUp,
  Lock,
  Crosshair,
  Filter,
} from 'lucide-react';
import { useTranslation, Trans } from 'react-i18next';
import { lunaApi } from '@/services/api';
import { useTelegramWebApp } from '@/hooks/useTelegramWebApp';
import { useUISounds } from '@/hooks/useUISounds';

// -----------------------------------------------------------------------------
// Типы данных с бэка
// -----------------------------------------------------------------------------
interface ApiGraphNode {
  id: number;
  telegramId: string | null;
  username: string | null;
  firstName: string | null;
  photoUrl: string | null;
  line: number;
  parentId: number | null;
  activeSlots: number;
  totalStaked: string;
  incomeFromUser: string;
  joinedAt: string;
  lastSeenAt: string | null;
  currentRank: number;
  highestStatus: number;
  quickStartOnly: boolean;
  totalContractsEver: number;
  pcActive: number;
  totalEarnedEver: string;
}

interface ApiGraphRoot {
  id: number;
  telegramId: string | null;
  username: string | null;
  firstName: string | null;
  photoUrl: string | null;
  joinedAt: string | null;
}

interface ApiGraphResponse {
  success: boolean;
  root: ApiGraphRoot;
  highestOpenLine: number;
  l1Count: number;
  maxLineCount: number;              // макс партнёров на одной линии
  maxLineLevel: number;              // какая линия перегружена
  serverFilterActive: boolean;
  serverFilterThreshold: number;
  hiddenInactiveCount: number;
  nodes: ApiGraphNode[];
  truncated: boolean;
}

// -----------------------------------------------------------------------------
// Node payload переданный в React Flow
// -----------------------------------------------------------------------------
interface UserNodeData extends Record<string, unknown> {
  username: string | null;
  firstName: string | null;
  photoUrl: string | null;
  telegramId: string | null;
  line: number;                 // 0 = root, 1-10 = ветки
  activeSlots: number;
  totalStaked: string;
  quickStartOnly: boolean;
  totalContractsEver: number;
  isInactive: boolean;          // нет слотов вообще (даже QS)
  currentRank: number;
  highestStatus: number;
  isRoot: boolean;
  isLocked: boolean;            // линия закрыта у root-юзера
  onOpen: (id: number) => void;
  userId: number;
}

// Цвет по «теплоте» (активный/QS-only/нет слота).
// Для закрытых линий — тусклая серо-красная рамка, замок.
function getHeatColor(data: UserNodeData): { border: string; glow: string } {
  if (data.isRoot) {
    return {
      border: 'hsl(50 90% 62%)',
      glow: 'hsl(50 90% 62% / 0.55)',
    };
  }
  if (data.isLocked) {
    return {
      border: 'hsl(0 0% 28%)',
      glow: 'transparent',
    };
  }
  if (data.activeSlots > 0) {
    return {
      border: 'hsl(150 75% 55%)',
      glow: 'hsl(150 75% 55% / 0.45)',
    };
  }
  // Всё остальное (QS-only, без слотов, неактивные) — серый
  return {
    border: 'hsl(0 0% 30%)',
    glow: 'transparent',
  };
}

// Имена рангов/статусов — синхронизированы с luna_rank_config и
// luna_status_config (см. backend/migrations/005_luna_status_rank.sql).
const RANK_NAMES: Record<number, string> = {
  1: 'Lunar',
  2: 'Stellar',
  3: 'Orbital',
  4: 'Galactic',
  5: 'Cosmic',
};

const STATUS_NAMES: Record<number, string> = {
  1: 'Cadet', 2: 'Pilot', 3: 'Navigator', 4: 'Commander',
  5: 'Captain', 6: 'Sentinel', 7: 'Warden', 8: 'Vanguard',
  9: 'Centurion', 10: 'Tribune', 11: 'Consul', 12: 'Prefect',
  13: 'Arbiter', 14: 'Legate', 15: 'Praetor', 16: 'Proconsul',
  17: 'Senator', 18: 'Chancellor', 19: 'Sovereign', 20: 'Imperator',
};

function displayName(
  n: { username: string | null; firstName: string | null },
  fallback: string = 'Partner',
): string {
  if (n.username) return `@${n.username}`;
  if (n.firstName) return n.firstName;
  return fallback;
}

function initials(n: { username: string | null; firstName: string | null }): string {
  const src = n.firstName || n.username || 'U';
  return src.slice(0, 1).toUpperCase();
}

// Фото юзеров грузим через наш backend-прокси (по аналогии с MoonRaid).
// Он сам обновит протухшую TG CDN ссылку через Bot API и отдаст картинку
// с правильным Content-Type и 1-часовым кэшем. Если telegramId нет —
// fallback на прямой URL (только корневые случаи, когда бэк не прислал).
function photoSrc(
  url: string | null,
  telegramId: string | null = null,
): string | null {
  if (telegramId) {
    return `/api/luna/user/photo/${telegramId}`;
  }
  return url;
}

// -----------------------------------------------------------------------------
// NodeBadge — маленькая прямоугольная плашка по углу аватарки
// -----------------------------------------------------------------------------
function NodeBadge({
  corner,
  text,
  color,
}: {
  corner: 'tl' | 'tr' | 'bl' | 'br';
  text: string;
  color: string;
}) {
  const pos: CSSProperties = {
    position: 'absolute',
    zIndex: 10,  // выше замка (3) и аватарки — всегда сверху
    background: '#0c0c18',
    color,
    border: `1px solid ${color}`,
    borderRadius: 8,
    fontSize: 8.5,
    padding: '1.5px 5px',
    fontWeight: 800,
    lineHeight: 1.2,
    letterSpacing: '0.02em',
    fontFamily: 'var(--font-mono, inherit)',
    boxShadow: '0 2px 6px rgba(0,0,0,0.7)',
    whiteSpace: 'nowrap',
    // Явно сбрасываем dim фильтр на случай если родитель его применил
    opacity: 1,
    filter: 'none',
  };
  if (corner === 'tl') {
    pos.top = -6;
    pos.left = -8;
  } else if (corner === 'tr') {
    pos.top = -6;
    pos.right = -8;
  } else if (corner === 'bl') {
    pos.bottom = -6;
    pos.left = -8;
  } else {
    pos.bottom = -6;
    pos.right = -8;
  }
  return <div style={pos}>{text}</div>;
}

// Компактное число: 1234 → 1.2K, 1234567 → 1.2M
function formatCompact(n: number): string {
  if (n >= 1_000_000) return (n / 1_000_000).toFixed(1).replace(/\.0$/, '') + 'M';
  if (n >= 1_000) return (n / 1_000).toFixed(1).replace(/\.0$/, '') + 'K';
  return Math.round(n).toString();
}

// -----------------------------------------------------------------------------
// Custom node — кругляш с аватаркой, именем снизу
// -----------------------------------------------------------------------------
function UserNode({ data, selected }: NodeProps<Node<UserNodeData>>) {
  const colors = getHeatColor(data);
  const photo = photoSrc(data.photoUrl, data.telegramId);
  const label = displayName(data);
  const size = data.isRoot ? 72 : 56;
  // Если <img> упал (404 от proxy / битый image) — переключаемся
  // на буквенный плейсхолдер. Иначе кружок остаётся пустым: bg
  // тёмный + img скрыт через display:none.
  const [imgError, setImgError] = useState(false);
  const showFallback = !photo || imgError;

  return (
    <div
      className="flex flex-col items-center"
      style={{
        cursor: 'pointer',
      }}
      onClick={() => data.onOpen(data.userId)}
    >
      {/* Target handle сверху — линия от родителя приходит сверху */}
      {!data.isRoot && (
        <Handle
          type="target"
          position={Position.Top}
          style={{ opacity: 0, pointerEvents: 'none' }}
        />
      )}

      {/* Avatar wrapper — position:relative без overflow:hidden,
          чтобы бейджи (L/R/S/$) не обрезались по кругу. */}
      <div
        style={{
          width: size,
          height: size,
          position: 'relative',
        }}
      >
        {/* Внутренний круг с обводкой + overflow:hidden ТОЛЬКО для фото.
            Dim применяется ТОЛЬКО здесь, чтобы бейджи и имя остались
            читаемыми на заблокированных нодах. */}
        <div
          style={{
            width: size,
            height: size,
            borderRadius: '50%',
            overflow: 'hidden',
            border: `2px solid ${colors.border}`,
            boxShadow: selected
              ? `0 0 0 3px ${colors.border}, 0 0 32px 8px ${colors.glow}`
              : `0 0 24px 2px ${colors.glow}`,
            background: 'linear-gradient(180deg, #1a1a28 0%, #0f0f18 100%)',
            transition: 'box-shadow 0.2s ease',
            opacity: data.isLocked ? 0.55 : 1,
            filter: data.isLocked ? 'grayscale(0.7)' : 'none',
          }}
        >
          {!showFallback && (
            // eslint-disable-next-line jsx-a11y/img-redundant-alt
            <img
              src={photo!}
              alt={label}
              style={{
                width: '100%',
                height: '100%',
                objectFit: 'cover',
                display: 'block',
              }}
              loading="lazy"
              onError={() => setImgError(true)}
            />
          )}
          {showFallback && (
            <div
              className="flex items-center justify-center"
              style={{
                width: '100%',
                height: '100%',
                color: colors.border,
                fontFamily: 'var(--font-display, inherit)',
                fontSize: size * 0.42,
                fontWeight: 800,
              }}
            >
              {initials(data)}
            </div>
          )}
        </div>

        {/* Замок — когда линия юзера закрыта у root'а */}
        {data.isLocked && (
          <div
            style={{
              position: 'absolute',
              inset: 0,
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              background: 'rgba(12,12,24,0.55)',
              color: 'hsl(0 60% 65%)',
              zIndex: 3,
              pointerEvents: 'none',
            }}
          >
            <Lock size={size * 0.32} strokeWidth={2.4} />
          </div>
        )}

        {/* Бейджи — 4 угла вокруг аватарки. Показываются только для не-root. */}
        {!data.isRoot && (
          <>
            {/* Top-right: линия (для locked — подсвечена красным) */}
            <NodeBadge
              corner="tr"
              text={`L${data.line}`}
              color={data.isLocked ? 'hsl(0 55% 55%)' : colors.border}
            />
            {/* Top-left: сумма staked (если > 0) */}
            {parseFloat(data.totalStaked) > 0 && (
              <NodeBadge
                corner="tl"
                text={`$${formatCompact(parseFloat(data.totalStaked))}`}
                color="hsl(150 75% 55%)"
              />
            )}
            {/* Bottom-right: ранг (если > 0) */}
            {data.currentRank > 0 && (
              <NodeBadge
                corner="br"
                text={`R${data.currentRank}`}
                color="hsl(45 95% 60%)"
              />
            )}
            {/* Bottom-left: статус (если > 0) */}
            {data.highestStatus > 0 && (
              <NodeBadge
                corner="bl"
                text={`S${data.highestStatus}`}
                color="hsl(280 70% 70%)"
              />
            )}
          </>
        )}
      </div>

      {/* Имя под аватаркой */}
      <div
        style={{
          marginTop: 6,
          maxWidth: size + 40,
          fontSize: 10,
          color: 'rgba(255,255,255,0.75)',
          fontWeight: 600,
          textAlign: 'center',
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          fontFamily: 'var(--font-mono, inherit)',
        }}
      >
        {label}
      </div>

      {/* Source handle снизу — от нас линии идут вниз к детям */}
      <Handle
        type="source"
        position={Position.Bottom}
        style={{ opacity: 0, pointerEvents: 'none' }}
      />
    </div>
  );
}

const NODE_TYPES = { userNode: UserNode };

// -----------------------------------------------------------------------------
// Dagre лейаут
// -----------------------------------------------------------------------------
function layoutGraph(
  nodes: Node[],
  edges: Edge[],
): { nodes: Node[]; edges: Edge[] } {
  const g = new dagre.graphlib.Graph();
  g.setDefaultEdgeLabel(() => ({}));
  g.setGraph({
    rankdir: 'TB',      // top → bottom: root сверху, дети вниз
    ranksep: 90,        // расстояние между уровнями
    nodesep: 40,        // расстояние между нодами одного уровня
    marginx: 40,
    marginy: 40,
  });

  for (const n of nodes) {
    g.setNode(n.id, { width: 100, height: 100 });
  }
  for (const e of edges) {
    g.setEdge(e.source, e.target);
  }

  dagre.layout(g);

  const placed: Node[] = nodes.map((n) => {
    const p = g.node(n.id);
    return {
      ...n,
      position: { x: p.x - 50, y: p.y - 50 },
      sourcePosition: Position.Bottom,
      targetPosition: Position.Top,
    };
  });

  return { nodes: placed, edges };
}

/**
 * Сортирует ноды так, чтобы dagre разложил L1 «центр-первый»:
 * ноды с самым большим поддеревом попадают в середину ряда, а
 * одиночки (только свой слот, без downline) — по краям. Также
 * рекурсивно сортирует детей каждой ноды по тому же принципу.
 *
 * Возвращает новые массивы nodes + edges в нужном порядке — dagre
 * использует этот порядок при минимизации перекрёстов.
 */
function prioritizeByImportance(
  nodes: Node[],
  edges: Edge[],
  rootId: string,
): { nodes: Node[]; edges: Edge[] } {
  const childrenOf = new Map<string, string[]>();
  for (const e of edges) {
    const arr = childrenOf.get(e.source) ?? [];
    arr.push(e.target);
    childrenOf.set(e.source, arr);
  }

  // Размер поддерева (кол-во потомков).
  const subtreeSize = (id: string): number => {
    let n = 0;
    const q = [id];
    while (q.length) {
      const cur = q.shift() as string;
      const kids = childrenOf.get(cur);
      if (!kids) continue;
      n += kids.length;
      q.push(...kids);
    }
    return n;
  };

  // Центр-первый reorder: sorted[0] → середина, sorted[1] → справа,
  // sorted[2] → слева, sorted[3] → правее, sorted[4] → левее ...
  const centerFirst = (ids: string[]): string[] => {
    if (ids.length <= 1) return ids;
    const sorted = [...ids].sort((a, b) => subtreeSize(b) - subtreeSize(a));
    const out = new Array<string>(sorted.length);
    const center = (sorted.length - 1) / 2;
    for (let i = 0; i < sorted.length; i++) {
      const offset = i === 0 ? 0
        : (i % 2 === 1 ? Math.ceil(i / 2) : -Math.ceil(i / 2));
      const slotIdx = Math.round(center + offset);
      out[slotIdx] = sorted[i];
    }
    return out;
  };

  // Переставляем детей у ROOT (и опционально у каждого родителя, чтоб
  // глубокие ветки тоже шли центр-первым).
  const newChildrenOf = new Map<string, string[]>();
  for (const [parentId, kids] of childrenOf.entries()) {
    newChildrenOf.set(
      parentId,
      parentId === rootId ? centerFirst(kids) : kids,
    );
  }

  // Обход BFS от root даёт топологически корректный порядок нод —
  // dagre использует его для tie-breaking при crossing minimization.
  const orderedNodeIds: string[] = [];
  const visited = new Set<string>();
  const queue = [rootId];
  while (queue.length) {
    const cur = queue.shift() as string;
    if (visited.has(cur)) continue;
    visited.add(cur);
    orderedNodeIds.push(cur);
    const kids = newChildrenOf.get(cur) ?? [];
    queue.push(...kids);
  }

  const nodeById = new Map(nodes.map((n) => [n.id, n] as const));
  const orderedNodes: Node[] = [];
  for (const id of orderedNodeIds) {
    const n = nodeById.get(id);
    if (n) orderedNodes.push(n);
  }
  // Докидываем потерянные (на всякий)
  for (const n of nodes) {
    if (!visited.has(n.id)) orderedNodes.push(n);
  }

  // Эджи тоже сортируем в порядке parent→first-child, ..., parent→last-child
  const orderedEdges: Edge[] = [];
  for (const [parentId, kids] of newChildrenOf.entries()) {
    for (const childId of kids) {
      const e = edges.find((ee) => ee.source === parentId && ee.target === childId);
      if (e) orderedEdges.push(e);
    }
  }

  return { nodes: orderedNodes, edges: orderedEdges };
}

// -----------------------------------------------------------------------------
// Модалка карточки юзера
// -----------------------------------------------------------------------------
function UserCard({
  node,
  allNodes,
  onClose,
}: {
  node: ApiGraphNode | null;
  allNodes: ApiGraphNode[];
  onClose: () => void;
}) {
  const { haptic } = useTelegramWebApp();
  const uiSounds = useUISounds();
  const { t, i18n } = useTranslation();
  const locale = i18n.language === 'ru' ? 'ru-RU' : 'en-US';
  // Если <img> упадёт (404 от photo-proxy / битый image) — показываем
  // буквенный плейсхолдер вместо пустого тёмного кружка.
  const [imgError, setImgError] = useState(false);
  useEffect(() => { setImgError(false); }, [node?.id]);

  // Расчёт структуры выбранного юзера: прямые L1, вся глубина,
  // оборот активных слотов в сабдереве.
  const subtreeStats = useMemo(() => {
    if (!node) {
      return { directPartners: 0, totalPartners: 0, activePartners: 0, subtreeTurnover: 0 };
    }
    // Карта parentId -> children
    const childrenOf = new Map<number, ApiGraphNode[]>();
    for (const n of allNodes) {
      if (n.parentId === null) continue;
      const arr = childrenOf.get(n.parentId) ?? [];
      arr.push(n);
      childrenOf.set(n.parentId, arr);
    }
    // BFS от node
    const queue: ApiGraphNode[] = [...(childrenOf.get(node.id) ?? [])];
    const directPartners = queue.length;
    let totalPartners = 0;
    let activePartners = 0;
    let subtreeTurnover = 0;
    while (queue.length > 0) {
      const u = queue.shift()!;
      totalPartners++;
      const staked = parseFloat(u.totalStaked);
      if (staked > 0 && !u.quickStartOnly) {
        activePartners++;
        subtreeTurnover += staked;
      }
      const kids = childrenOf.get(u.id);
      if (kids) queue.push(...kids);
    }
    return { directPartners, totalPartners, activePartners, subtreeTurnover };
  }, [node, allNodes]);

  if (!node) return null;

  const fallback = t('networkTree.fallbackPartner');
  const label = displayName(node, fallback);
  const joined = new Date(node.joinedAt).toLocaleDateString(locale, {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });

  const stakedNum = parseFloat(node.totalStaked);
  const incomeNum = parseFloat(node.incomeFromUser);
  const earnedEverNum = parseFloat(node.totalEarnedEver);

  const inviter: { label: string } = (() => {
    if (node.parentId === null) {
      return { label: t('networkTree.card.inviterYou') };
    }
    const p = allNodes.find((n) => n.id === node.parentId);
    if (p) return { label: displayName(p, fallback) };
    return { label: t('networkTree.card.inviterUnknown') };
  })();

  const lastSeenLabel = (() => {
    if (!node.lastSeenAt) return t('networkTree.card.lastSeenDash');
    const diffMs = Date.now() - new Date(node.lastSeenAt).getTime();
    const days = Math.floor(diffMs / (24 * 3600 * 1000));
    if (days <= 0) return t('networkTree.card.lastSeenToday');
    if (days === 1) return t('networkTree.card.lastSeenYesterday');
    if (days < 30) return t('networkTree.card.lastSeenDaysAgo', { days });
    const months = Math.floor(days / 30);
    return t('networkTree.card.lastSeenMonthsAgo', { months });
  })();

  const openTg = () => {
    haptic.light();
    // openTelegramLink не в нашей типизации — кастуем.
    const tg = (window as unknown as {
      Telegram?: { WebApp?: { openTelegramLink?: (u: string) => void } };
    }).Telegram?.WebApp;
    if (node.username) {
      tg?.openTelegramLink?.(`https://t.me/${node.username}`);
    } else {
      tg?.openTelegramLink?.(`tg://user?id=${node.id}`);
    }
  };

  const handleClose = () => {
    uiSounds.bellClose();
    onClose();
  };

  return (
    <motion.div
      className="fixed inset-0 z-[60] flex items-center justify-center px-4"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.22 }}
      onClick={handleClose}
    >
      <div className="absolute inset-0 bg-black/70" />
      <motion.div
        className="relative w-full max-w-sm rounded-[26px] p-5"
        style={{
          background:
            'linear-gradient(180deg, rgba(28,28,42,0.98) 0%, rgba(18,18,30,0.98) 100%)',
          border: '1px solid var(--glass-stroke-strong)',
          boxShadow: 'var(--glass-shadow)',
        }}
        initial={{ y: 30, opacity: 0, scale: 0.94 }}
        animate={{ y: 0, opacity: 1, scale: 1 }}
        exit={{ y: 30, opacity: 0, scale: 0.94 }}
        transition={{ duration: 0.28, ease: [0.32, 0.72, 0, 1] }}
        onClick={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          aria-label={t('networkTree.closeAria')}
          onClick={handleClose}
          className="absolute top-3 right-3 h-8 w-8 rounded-full lg-pill flex items-center justify-center"
        >
          <X size={14} />
        </button>

        {/* Avatar + name */}
        <div className="flex items-center gap-4 mb-4">
          <div
            className="flex-shrink-0 rounded-full overflow-hidden"
            style={{
              width: 72,
              height: 72,
              border: '2px solid hsl(150 75% 55%)',
              background: '#0f0f18',
            }}
          >
            {photoSrc(node.photoUrl, node.telegramId) && !imgError ? (
              <img
                src={photoSrc(node.photoUrl, node.telegramId) ?? ''}
                alt={label}
                style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                onError={() => setImgError(true)}
              />
            ) : (
              <div
                className="flex items-center justify-center w-full h-full"
                style={{ color: 'hsl(150 75% 55%)', fontSize: 32, fontWeight: 800 }}
              >
                {initials(node)}
              </div>
            )}
          </div>
          <div className="min-w-0 flex-1">
            <p className="text-[11px] uppercase tracking-[0.18em] text-[var(--text-muted)] mb-1 font-mono">
              {t('networkTree.card.lineAndId', { line: node.line, id: node.id })}
            </p>
            <h3 className="font-display text-[18px] font-extrabold text-[var(--text-primary)] truncate">
              {label}
            </h3>
            <p className="text-[11px] text-[var(--text-muted)] mt-0.5">
              {t('networkTree.card.joinedActivity', { joined, lastSeen: lastSeenLabel })}
            </p>
          </div>
        </div>

        <div
          className="rounded-xl px-3 py-2 mb-3 flex items-center justify-between gap-2"
          style={{
            background: 'var(--glass-fill)',
            border: '1px solid var(--glass-stroke)',
          }}
        >
          <span className="text-[10px] uppercase tracking-[0.14em] text-[var(--text-muted)] font-mono font-bold">
            {t('networkTree.card.inviter')}
          </span>
          <span className="text-[12px] font-bold text-[var(--text-primary)] truncate">
            {inviter.label}
          </span>
        </div>

        <p className="text-[9px] uppercase tracking-[0.18em] text-[var(--text-muted)] font-mono mb-1.5 mt-1">
          {t('networkTree.card.theirAssets')}
        </p>
        <div className="grid grid-cols-2 gap-2 mb-3">
          <StatMini
            icon={<Zap size={13} />}
            label={t('networkTree.card.activeSlots')}
            value={node.activeSlots.toString()}
          />
          <StatMini
            icon={<Users size={13} />}
            label={t('networkTree.card.working')}
            value={stakedNum > 0 ? stakedNum.toLocaleString(locale) : '—'}
            suffix={stakedNum > 0 ? 'USD₮' : ''}
          />
          <StatMini
            icon={<Zap size={13} />}
            label={t('networkTree.card.totalContracts')}
            value={node.totalContractsEver.toString()}
          />
          <StatMini
            icon={<Zap size={13} />}
            label={t('networkTree.card.plasmaCore')}
            value={node.pcActive > 0 ? t('networkTree.card.pcTimes', { n: node.pcActive }) : '—'}
          />
        </div>

        {(node.currentRank > 0 || node.highestStatus > 0) && (
          <>
            <p className="text-[9px] uppercase tracking-[0.18em] text-[var(--text-muted)] font-mono mb-1.5 mt-2">
              {t('networkTree.card.achievements')}
            </p>
            <div className="grid grid-cols-2 gap-2 mb-3">
              {node.currentRank > 0 && (
                <StatMini
                  icon={<Trophy size={13} />}
                  label={t('networkTree.card.rank')}
                  value={RANK_NAMES[node.currentRank] ?? `R${node.currentRank}`}
                  suffix={`R${node.currentRank}`}
                />
              )}
              {node.highestStatus > 0 && (
                <StatMini
                  icon={<Trophy size={13} />}
                  label={t('networkTree.card.leaderStatus')}
                  value={STATUS_NAMES[node.highestStatus] ?? `S${node.highestStatus}`}
                  suffix={`S${node.highestStatus}`}
                />
              )}
            </div>
          </>
        )}

        <p className="text-[9px] uppercase tracking-[0.18em] text-[var(--text-muted)] font-mono mb-1.5 mt-2">
          {t('networkTree.card.theirStructure')}
        </p>
        <div className="grid grid-cols-2 gap-2 mb-3">
          <StatMini
            icon={<Users size={13} />}
            label={t('networkTree.card.directL1')}
            value={subtreeStats.directPartners.toString()}
          />
          <StatMini
            icon={<Users size={13} />}
            label={t('networkTree.card.totalDepth')}
            value={subtreeStats.totalPartners.toString()}
          />
          <StatMini
            icon={<Zap size={13} />}
            label={t('networkTree.card.active')}
            value={subtreeStats.activePartners.toString()}
          />
          <StatMini
            icon={<TrendingUp size={13} />}
            label={t('networkTree.card.turnover')}
            value={
              subtreeStats.subtreeTurnover > 0
                ? formatCompact(subtreeStats.subtreeTurnover)
                : '—'
            }
            suffix={subtreeStats.subtreeTurnover > 0 ? 'USD₮' : ''}
          />
        </div>

        {earnedEverNum > 0 && (
          <div
            className="rounded-xl p-3 mb-3"
            style={{
              background:
                'color-mix(in oklab, var(--accent-secondary) 10%, transparent)',
              border:
                '1px solid color-mix(in oklab, var(--accent-secondary) 25%, transparent)',
            }}
          >
            <p className="text-[9px] uppercase tracking-[0.16em] text-[var(--text-muted)] font-mono">
              {t('networkTree.card.earnedTotal')}
            </p>
            <p className="text-[16px] font-extrabold text-[var(--accent-secondary)] mt-0.5">
              {earnedEverNum.toLocaleString(locale, { maximumFractionDigits: 2 })}
              <span className="text-[11px] text-[var(--text-muted)] ml-1 font-medium">
                USD₮
              </span>
            </p>
          </div>
        )}

        <div
          className="rounded-xl p-3 mb-4"
          style={{
            background: 'color-mix(in oklab, var(--accent-primary) 10%, transparent)',
            border: '1px solid color-mix(in oklab, var(--accent-primary) 25%, transparent)',
          }}
        >
          <p className="text-[10px] uppercase tracking-[0.16em] text-[var(--text-muted)] font-mono">
            {t('networkTree.card.yourIncomeFromThem')}
          </p>
          <p className="text-[20px] font-extrabold text-[var(--accent-primary)] mt-1">
            {incomeNum.toLocaleString(locale, { maximumFractionDigits: 2 })}
            <span className="text-[12px] text-[var(--text-muted)] ml-1 font-medium">
              USD₮
            </span>
          </p>
        </div>

        <button
          type="button"
          onClick={openTg}
          className="w-full rounded-full py-2.5 text-[13px] font-extrabold transition active:scale-[0.98]"
          style={{
            background: 'color-mix(in oklab, var(--accent-secondary) 20%, transparent)',
            border: '1px solid color-mix(in oklab, var(--accent-secondary) 40%, transparent)',
            color: 'var(--accent-secondary)',
          }}
        >
          {t('networkTree.card.openInTelegram')}
        </button>
      </motion.div>
    </motion.div>
  );
}

function StatMini({
  icon,
  label,
  value,
  suffix,
}: {
  icon: React.ReactNode;
  label: string;
  value: string;
  suffix?: string;
}) {
  return (
    <div
      className="rounded-xl px-2.5 py-2"
      style={{
        background: 'var(--glass-fill)',
        border: '1px solid var(--glass-stroke)',
      }}
    >
      <div className="flex items-center gap-1 text-[var(--text-muted)] mb-1">
        {icon}
        <span className="text-[8px] uppercase tracking-[0.1em] font-mono font-bold">
          {label}
        </span>
      </div>
      <p className="text-[13px] font-extrabold text-[var(--text-primary)] truncate">
        {value}
        {suffix ? (
          <span className="text-[9px] text-[var(--text-muted)] ml-0.5 font-medium">
            {suffix}
          </span>
        ) : null}
      </p>
    </div>
  );
}

// -----------------------------------------------------------------------------
// Внутренний компонент — имеет доступ к useReactFlow hooks
// -----------------------------------------------------------------------------
const LS_KEY = 'luna.networkTree.settings.v1';

// Порог авто-фильтра: если в L1 больше этого значения — авто-скрываем
// неактивных. Юзер может явно переключить — тогда его выбор сохраняется.
const AUTO_FILTER_L1_THRESHOLD = 20;

interface TreeSettings {
  // null — юзер ещё не трогал, работает авто-режим (L1 > 20 → hide)
  // boolean — явный выбор юзера, авто-режим выключен
  hideInactive: boolean | null;
}

function loadSettings(): TreeSettings {
  try {
    const raw = localStorage.getItem(LS_KEY);
    if (!raw) return { hideInactive: null };
    const parsed = JSON.parse(raw);
    if (typeof parsed?.hideInactive === 'boolean') {
      return { hideInactive: parsed.hideInactive };
    }
    return { hideInactive: null };
  } catch {
    return { hideInactive: null };
  }
}

function saveSettings(s: TreeSettings): void {
  try {
    localStorage.setItem(LS_KEY, JSON.stringify(s));
  } catch {
    /* ignore quota / private mode */
  }
}

function NetworkTreeInner() {
  const navigate = useNavigate();
  const { haptic } = useTelegramWebApp();
  const uiSounds = useUISounds();
  const { setCenter, fitView } = useReactFlow();
  const { t } = useTranslation();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>('');
  const [data, setData] = useState<ApiGraphResponse | null>(null);
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchHit, setSearchHit] = useState(false);

  // Настройки визуализации — сохраняются в localStorage.
  const [settings, setSettingsState] = useState<TreeSettings>(() => loadSettings());
  const updateSettings = useCallback((patch: Partial<TreeSettings>) => {
    setSettingsState((prev) => {
      const next = { ...prev, ...patch };
      saveSettings(next);
      return next;
    });
  }, []);

  // Когда бэк уже отсёк неактивных — клиентский фильтр блокируется
  // (нечего дополнительно скрывать, всё уже отфильтровано на сервере).
  const serverFilterActive = data?.serverFilterActive ?? false;
  const l1Count = data?.l1Count ?? 0;
  // Общая команда (все линии) — при малых командах фильтр
  // ПРИНУДИТЕЛЬНО выключен: админ явно сказал, что до 20 чел
  // никакого авто-скрытия не должно быть.
  const totalTeamSize = data?.nodes.length ?? 0;
  const smallTeam = totalTeamSize < AUTO_FILTER_L1_THRESHOLD;

  // Эффективное значение клиентского фильтра:
  //   - бэк-фильтр активен → принудительно "скрыты"
  //   - команда < 20 и юзер явно не включил → показываем всех
  //   - иначе: выбор юзера или авто по L1
  let hideInactiveEffective: boolean;
  if (serverFilterActive) {
    hideInactiveEffective = true;
  } else if (smallTeam && settings.hideInactive !== true) {
    // Малая команда + юзер явно не включил → всегда выкл
    hideInactiveEffective = false;
  } else {
    hideInactiveEffective =
      settings.hideInactive ?? l1Count > AUTO_FILTER_L1_THRESHOLD;
  }
  const isAutoMode =
    !serverFilterActive && settings.hideInactive === null;

  // Загружаем граф. Если у кого-то ещё нет photoUrl — бэк кикнул
  // скачивание в фоне; через 4с перезапросим чтоб забрать свежие
  // аватарки (одна автоматическая попытка).
  useEffect(() => {
    let cancelled = false;
    let retryTimer: ReturnType<typeof setTimeout> | undefined;

    const load = async (isRetry: boolean) => {
      try {
        const res = await lunaApi.referrals.graph();
        if (cancelled) return;
        const body = res.data as ApiGraphResponse;
        setData(body);

        // Планируем авторефетч только при первой загрузке и только
        // если есть ноды без photoUrl.
        if (!isRetry) {
          const missing =
            !body.root.photoUrl ||
            body.nodes.some((n) => !n.photoUrl);
          if (missing) {
            retryTimer = setTimeout(() => {
              if (!cancelled) load(true);
            }, 4000);
          }
        }
      } catch (err) {
        if (cancelled) return;
        setError((err as Error).message || t('networkTree.loadError'));
      } finally {
        if (!cancelled && !isRetry) setLoading(false);
      }
    };

    load(false);

    return () => {
      cancelled = true;
      if (retryTimer) clearTimeout(retryTimer);
    };
  }, []);

  // Обработчик клика — сохраняем выбранного
  const handleNodeOpen = useCallback(
    (userId: number) => {
      haptic.light();
      setSelectedId(userId);
    },
    [haptic],
  );

  // Строим React Flow ноды + эджы + лейаут
  const { nodes, edges } = useMemo(() => {
    if (!data) return { nodes: [] as Node[], edges: [] as Edge[] };

    const rootId = `u-${data.root.id}`;
    const hOpen = data.highestOpenLine ?? 10;

    const rootNode: Node<UserNodeData> = {
      id: rootId,
      type: 'userNode',
      position: { x: 0, y: 0 },
      data: {
        userId: data.root.id,
        username: data.root.username,
        firstName: data.root.firstName,
        photoUrl: data.root.photoUrl,
        telegramId: data.root.telegramId,
        line: 0,
        activeSlots: 0,
        totalStaked: '0',
        quickStartOnly: false,
        totalContractsEver: 0,
        isInactive: false,
        currentRank: 0,
        highestStatus: 0,
        isRoot: true,
        isLocked: false,
        onOpen: () => {},
      },
    };

    // Фильтр «скрыть неактивных»: юзер полностью пуст, если у него
    // нет ни одного контракта за всё время. Авто-включается при L1 > 20.
    const filtered = hideInactiveEffective
      ? data.nodes.filter((n) => n.totalContractsEver > 0)
      : data.nodes;
    const visibleIds = new Set(filtered.map((n) => n.id));

    const userNodes: Node<UserNodeData>[] = filtered.map((n) => ({
      id: `u-${n.id}`,
      type: 'userNode',
      position: { x: 0, y: 0 },
      data: {
        userId: n.id,
        username: n.username,
        firstName: n.firstName,
        photoUrl: n.photoUrl,
        telegramId: n.telegramId,
        line: n.line,
        activeSlots: n.activeSlots,
        totalStaked: n.totalStaked,
        quickStartOnly: n.quickStartOnly,
        totalContractsEver: n.totalContractsEver,
        isInactive: n.activeSlots === 0 && n.totalContractsEver === 0,
        currentRank: n.currentRank,
        highestStatus: n.highestStatus,
        isRoot: false,
        isLocked: n.line > hOpen,
        onOpen: handleNodeOpen,
      },
    }));

    const allNodes: Node<UserNodeData>[] = [rootNode, ...userNodes];

    const edgeList: Edge[] = filtered
      .filter((n) => {
        // Родитель существует в выводе: либо root, либо в filtered.
        if (n.parentId === null) return true;
        return visibleIds.has(n.parentId);
      })
      .map((n) => {
        const locked = n.line > hOpen;
        return {
          id: `e-${n.id}`,
          source: n.parentId ? `u-${n.parentId}` : rootId,
          target: `u-${n.id}`,
          type: 'straight',
          animated: false,
          style: {
            stroke: locked
              ? 'hsla(0, 0%, 35%, 0.3)'
              : 'hsla(180, 45%, 55%, 0.4)',
            strokeWidth: 1.4,
          },
        };
      });

    // Сортируем ноды/эджи так, чтобы L1 с самым жирным поддеревом
    // попал в центр (сортировка ДО dagre — он учтёт порядок при
    // минимизации перекрёстов).
    const prioritized = prioritizeByImportance(
      allNodes as Node[],
      edgeList,
      rootId,
    );
    const laid = layoutGraph(prioritized.nodes, prioritized.edges);
    return laid;
  }, [data, handleNodeOpen, hideInactiveEffective]);

  // Авто-fit при первой загрузке графа
  useEffect(() => {
    if (!loading && nodes.length > 0) {
      setTimeout(() => fitView({ padding: 0.2, duration: 400 }), 50);
    }
  }, [loading, nodes.length, fitView]);

  // Поиск
  const doSearch = useCallback(() => {
    const q = searchQuery.trim().toLowerCase().replace(/^@/, '');
    if (!q || !data) {
      setSearchHit(false);
      return;
    }
    // Поиск: username, first_name, or numeric id
    const hit = data.nodes.find((n) => {
      if (n.username?.toLowerCase() === q) return true;
      if (n.username?.toLowerCase().includes(q)) return true;
      if (n.firstName?.toLowerCase().includes(q)) return true;
      if (String(n.id) === q) return true;
      return false;
    });
    if (!hit) {
      setSearchHit(false);
      return;
    }
    setSearchHit(true);
    setSelectedId(hit.id);
    // Центрируем камеру
    const rfNode = nodes.find((n) => n.id === `u-${hit.id}`);
    if (rfNode) {
      setCenter(rfNode.position.x + 50, rfNode.position.y + 50, {
        zoom: 1.2,
        duration: 600,
      });
    }
  }, [searchQuery, data, nodes, setCenter]);

  const selectedNode = useMemo(
    () => data?.nodes.find((n) => n.id === selectedId) ?? null,
    [data, selectedId],
  );

  // Центрировать камеру на самом юзере (root-ноде)
  const centerOnSelf = useCallback(() => {
    haptic.light();
    uiSounds.tap();
    const rootId = data ? `u-${data.root.id}` : null;
    const rootNode = nodes.find((n) => n.id === rootId);
    if (rootNode) {
      setCenter(rootNode.position.x + 50, rootNode.position.y + 50, {
        zoom: 1.3,
        duration: 520,
      });
    } else {
      fitView({ padding: 0.2, duration: 400 });
    }
  }, [data, nodes, setCenter, fitView, haptic, uiSounds]);

  // Подсказка «фильтр заблокирован» — показываем поп-ап на 4 сек,
  // когда юзер тыкает заблокированную кнопку.
  const [filterLockHint, setFilterLockHint] = useState(false);
  const filterLockTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    return () => {
      if (filterLockTimerRef.current) clearTimeout(filterLockTimerRef.current);
    };
  }, []);

  const toggleFilterInactive = useCallback(() => {
    // Если бэк уже фильтрует — показываем подсказку и выходим.
    if (serverFilterActive) {
      haptic.medium();
      uiSounds.toastError();
      setFilterLockHint(true);
      if (filterLockTimerRef.current) clearTimeout(filterLockTimerRef.current);
      filterLockTimerRef.current = setTimeout(() => {
        setFilterLockHint(false);
      }, 4500);
      return;
    }
    haptic.light();
    uiSounds.tap();
    // Переключаем относительно ТЕКУЩЕГО эффективного значения, чтобы
    // из авто-режима юзер попадал в осмысленное состояние одним тапом.
    updateSettings({ hideInactive: !hideInactiveEffective });
  }, [haptic, uiSounds, hideInactiveEffective, updateSettings, serverFilterActive]);

  const partnerCount = data?.nodes.length ?? 0;

  const headerStyle: CSSProperties = {
    background:
      'linear-gradient(180deg, rgba(12,12,24,0.88) 0%, rgba(12,12,24,0.6) 100%)',
    backdropFilter: 'blur(12px)',
    WebkitBackdropFilter: 'blur(12px)',
    borderBottom: '1px solid var(--glass-stroke)',
  };

  return (
    <div className="fixed inset-0 z-[40] bg-[var(--bg-base)]">
      {/* Header */}
      <div
        className="absolute top-0 left-0 right-0 z-10 px-4 pt-[max(env(safe-area-inset-top),16px)] pb-3 flex items-center gap-3"
        style={headerStyle}
      >
        <button
          type="button"
          onClick={() => navigate('/network')}
          className="h-10 w-10 rounded-2xl flex items-center justify-center transition active:scale-90"
          style={{
            background: 'var(--glass-fill)',
            border: '1px solid var(--glass-stroke)',
          }}
          aria-label={t('networkTree.backAria')}
        >
          <ArrowLeft size={18} className="text-[var(--text-secondary)]" />
        </button>
        <div className="flex-1 min-w-0">
          <p className="text-[9px] uppercase tracking-[0.2em] text-[var(--text-muted)] font-mono">
            {t('networkTree.title')}
          </p>
          <h1 className="font-display text-[16px] font-extrabold text-[var(--text-primary)] truncate">
            {loading ? t('networkTree.loading') : t('networkTree.partners', { count: partnerCount })}
          </h1>
          {serverFilterActive && (data?.hiddenInactiveCount ?? 0) > 0 && (
            <p className="text-[10px] text-[var(--text-muted)] mt-0.5 truncate">
              {t('networkTree.hiddenInactive', {
                hidden: data?.hiddenInactiveCount,
                level: data?.maxLineLevel,
                count: data?.maxLineCount,
              })}
            </p>
          )}
        </div>
      </div>

      {/* Search bar */}
      <div
        className="absolute left-4 right-4 z-10 flex items-center gap-2"
        style={{ top: 'calc(max(env(safe-area-inset-top), 16px) + 64px)' }}
      >
        <div
          className="flex-1 flex items-center gap-2 rounded-full px-3 py-2"
          style={{
            background: 'var(--glass-fill)',
            border: '1px solid var(--glass-stroke)',
            backdropFilter: 'blur(8px)',
            WebkitBackdropFilter: 'blur(8px)',
          }}
        >
          <Search size={14} className="text-[var(--text-muted)] flex-shrink-0" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && doSearch()}
            placeholder={t('networkTree.searchPlaceholder')}
            className="flex-1 bg-transparent outline-none text-[12px] text-[var(--text-primary)] placeholder:text-[var(--text-muted)]"
          />
          {searchQuery && (
            <button
              type="button"
              onClick={() => {
                setSearchQuery('');
                setSearchHit(false);
              }}
              className="text-[var(--text-muted)] flex-shrink-0"
            >
              <X size={13} />
            </button>
          )}
        </div>
        <button
          type="button"
          onClick={doSearch}
          className="rounded-full px-4 py-2 text-[12px] font-bold transition active:scale-95"
          style={{
            background: 'color-mix(in oklab, var(--accent-primary) 18%, transparent)',
            border: '1px solid color-mix(in oklab, var(--accent-primary) 35%, transparent)',
            color: 'var(--accent-primary)',
          }}
        >
          {t('networkTree.findBtn')}
        </button>
      </div>

      {searchQuery && !searchHit && data && (
        <div
          className="absolute z-10 left-4 right-4 rounded-xl px-3 py-2"
          style={{
            top: 'calc(max(env(safe-area-inset-top), 16px) + 120px)',
            background: 'var(--glass-fill)',
            border: '1px solid var(--glass-stroke)',
            color: 'var(--text-muted)',
            fontSize: 11,
          }}
        >
          {t('networkTree.nothingFound')}
        </div>
      )}

      {/* Graph canvas — без блока прогресса */}
      <div
        className="absolute inset-0 transition-[padding] duration-200"
        style={{
          paddingTop: 'calc(max(env(safe-area-inset-top), 16px) + 116px)',
        }}
      >
        {loading ? (
          <div className="flex items-center justify-center h-full text-[var(--text-muted)] text-[13px]">
            {t('networkTree.buildingTree')}
          </div>
        ) : error ? (
          <div className="flex items-center justify-center h-full text-[var(--status-negative)] text-[13px] px-8 text-center">
            {error}
          </div>
        ) : nodes.length <= 1 ? (
          <div className="flex flex-col items-center justify-center h-full text-[var(--text-muted)] text-[13px] px-8 text-center gap-3">
            <Users size={40} className="opacity-40" />
            <p>{t('networkTree.noPartners')}</p>
            <p className="text-[11px]">
              {t('networkTree.shareHint')}
            </p>
          </div>
        ) : (
          <ReactFlow
            nodes={nodes}
            edges={edges}
            nodeTypes={NODE_TYPES}
            fitView
            minZoom={0.2}
            maxZoom={2.5}
            panOnDrag
            panOnScroll={false}
            zoomOnScroll
            zoomOnPinch
            zoomOnDoubleClick={false}
            proOptions={{ hideAttribution: true }}
          >
            <Background
              variant={BackgroundVariant.Dots}
              gap={24}
              size={1}
              color="rgba(255,255,255,0.06)"
            />
            <MiniMap
              nodeColor={(node) => {
                const d = node.data as UserNodeData;
                if (d.isRoot) return 'hsl(50 90% 62%)';
                if (d.activeSlots > 0) return 'hsl(150 75% 55%)';
                return 'hsl(0 0% 35%)';
              }}
              nodeStrokeColor="rgba(255,255,255,0.2)"
              nodeStrokeWidth={1}
              nodeBorderRadius={20}
              // маска — тёмное полотно ВОКРУГ видимой области.
              // Видимая область остаётся прозрачной и обводится жёлтым.
              maskColor="rgba(0,0,0,0.65)"
              maskStrokeColor="hsla(50, 90%, 62%, 0.55)"
              maskStrokeWidth={1.5}
              pannable
              zoomable
              ariaLabel={t('networkTree.miniMapAria')}
              style={{
                background: 'rgba(10,10,20,0.9)',
                border: '1px solid var(--glass-stroke)',
                borderRadius: 12,
                width: 130,
                height: 100,
              }}
            />
          </ReactFlow>
        )}
      </div>

      {/* Подсказка о заблокированном фильтре */}
      <AnimatePresence>
        {filterLockHint && (
          <motion.div
            initial={{ opacity: 0, y: 10, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 10, scale: 0.96 }}
            transition={{ duration: 0.22, ease: [0.32, 0.72, 0, 1] }}
            className="absolute z-30 rounded-2xl p-3"
            style={{
              right: 16,
              bottom: 'calc(max(env(safe-area-inset-bottom), 16px) + 168px)',
              maxWidth: 280,
              background:
                'linear-gradient(180deg, rgba(28,28,42,0.98) 0%, rgba(18,18,30,0.98) 100%)',
              border: '1px solid color-mix(in oklab, var(--accent-primary) 45%, var(--glass-stroke-strong))',
              boxShadow: '0 12px 40px -8px rgba(0,0,0,0.7)',
            }}
          >
            <div className="flex items-start gap-2">
              <div
                style={{
                  width: 22,
                  height: 22,
                  borderRadius: '50%',
                  background: 'color-mix(in oklab, var(--accent-primary) 22%, transparent)',
                  border: '1px solid var(--accent-primary)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: 'var(--accent-primary)',
                  flexShrink: 0,
                  marginTop: 1,
                }}
              >
                <Lock size={11} strokeWidth={2.4} />
              </div>
              <div className="min-w-0">
                <p className="text-[11px] font-bold text-[var(--text-primary)] mb-1 leading-[1.35]">
                  {t('networkTree.filterLock.title')}
                </p>
                <p className="text-[11px] text-[var(--text-muted)] leading-[1.4]">
                  <Trans
                    i18nKey="networkTree.filterLock.body"
                    values={{
                      level: data?.maxLineLevel ?? 1,
                      count: data?.maxLineCount ?? 0,
                      threshold: data?.serverFilterThreshold ?? 20,
                    }}
                    components={{ b: <strong />, br: <br /> }}
                  />
                  {(data?.hiddenInactiveCount ?? 0) > 0 && (
                    <Trans
                      i18nKey="networkTree.filterLock.hidden"
                      values={{ hidden: data?.hiddenInactiveCount }}
                      components={{ b: <strong /> }}
                    />
                  )}
                </p>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Controls — над миникартой: центр + фильтр */}
      {!loading && !error && nodes.length > 1 && (
        <div
          className="absolute z-20 flex flex-col gap-2"
          style={{
            right: 16,
            // миникарта 100 высоты + 16px снизу (safe area) → стэк выше
            bottom: 'calc(max(env(safe-area-inset-bottom), 16px) + 116px)',
          }}
        >
          <button
            type="button"
            onClick={toggleFilterInactive}
            aria-label={
              serverFilterActive
                ? t('networkTree.filterAria.serverLocked', { threshold: data?.serverFilterThreshold ?? 20 })
                : hideInactiveEffective
                  ? t('networkTree.filterAria.showAll')
                  : t('networkTree.filterAria.hideInactive')
            }
            title={
              serverFilterActive
                ? t('networkTree.filterTitle.serverLocked', {
                    hidden: data?.hiddenInactiveCount ?? 0,
                    level: data?.maxLineLevel,
                    count: data?.maxLineCount,
                    threshold: data?.serverFilterThreshold ?? 20,
                  })
                : isAutoMode
                  ? t('networkTree.filterTitle.auto', { threshold: AUTO_FILTER_L1_THRESHOLD })
                  : hideInactiveEffective
                    ? t('networkTree.filterTitle.hidden')
                    : t('networkTree.filterTitle.shown')
            }
            className="h-9 w-9 rounded-full flex items-center justify-center transition active:scale-90 relative"
            style={{
              background: hideInactiveEffective
                ? 'color-mix(in oklab, var(--accent-primary) 22%, transparent)'
                : 'var(--glass-fill)',
              border: `1px solid ${hideInactiveEffective
                ? 'color-mix(in oklab, var(--accent-primary) 50%, transparent)'
                : 'var(--glass-stroke)'}`,
              color: hideInactiveEffective
                ? 'var(--accent-primary)'
                : 'var(--text-secondary)',
              backdropFilter: 'blur(8px)',
              WebkitBackdropFilter: 'blur(8px)',
              cursor: serverFilterActive ? 'not-allowed' : 'pointer',
              opacity: serverFilterActive ? 0.75 : 1,
            }}
          >
            <Filter size={15} strokeWidth={2} />
            {/* Маленькая лапша в углу: замок если serverFilter, точка если auto */}
            {serverFilterActive ? (
              <span
                style={{
                  position: 'absolute',
                  top: -4,
                  right: -4,
                  width: 14,
                  height: 14,
                  borderRadius: '50%',
                  background: '#0c0c18',
                  border: '1px solid var(--accent-primary)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: 'var(--accent-primary)',
                }}
                aria-hidden
              >
                <Lock size={7} strokeWidth={3} />
              </span>
            ) : isAutoMode && hideInactiveEffective ? (
              <span
                style={{
                  position: 'absolute',
                  top: -3,
                  right: -3,
                  width: 7,
                  height: 7,
                  borderRadius: '50%',
                  background: 'var(--accent-primary)',
                  boxShadow: '0 0 6px var(--accent-primary)',
                }}
                aria-hidden
              />
            ) : null}
          </button>
          <button
            type="button"
            onClick={centerOnSelf}
            aria-label={t('networkTree.centerOnMeAria')}
            className="h-9 w-9 rounded-full flex items-center justify-center transition active:scale-90"
            style={{
              background: 'var(--glass-fill)',
              border: '1px solid var(--glass-stroke)',
              color: 'var(--text-secondary)',
              backdropFilter: 'blur(8px)',
              WebkitBackdropFilter: 'blur(8px)',
            }}
          >
            <Crosshair size={15} strokeWidth={2} />
          </button>
        </div>
      )}

      {/* Legend — компактная плашка внизу с цветами */}
      <Legend />

      {/* User card modal */}
      <AnimatePresence>
        {selectedNode && (
          <UserCard
            node={selectedNode}
            allNodes={data?.nodes ?? []}
            onClose={() => setSelectedId(null)}
          />
        )}
      </AnimatePresence>
    </div>
  );
}

// -----------------------------------------------------------------------------
// Legend — расшифровка цветов + подписей ⚡/QS/R/S
// -----------------------------------------------------------------------------
function Legend() {
  const [open, setOpen] = useState(false);
  const { t } = useTranslation();
  return (
    <div
      className="absolute z-10"
      style={{
        left: 16,
        bottom: 'calc(max(env(safe-area-inset-bottom), 12px) + 12px)',
      }}
    >
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex items-center gap-2 rounded-full px-3 py-1.5 text-[11px] font-mono"
        style={{
          background: 'var(--glass-fill)',
          border: '1px solid var(--glass-stroke)',
          backdropFilter: 'blur(8px)',
          WebkitBackdropFilter: 'blur(8px)',
          color: 'var(--text-secondary)',
        }}
      >
        <span
          style={{
            width: 8,
            height: 8,
            borderRadius: '50%',
            background:
              'linear-gradient(90deg, hsl(50 90% 62%) 0%, hsl(150 75% 55%) 100%)',
          }}
        />
        {t('networkTree.legend.toggle')}
      </button>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 8 }}
            transition={{ duration: 0.18 }}
            className="absolute bottom-[40px] left-0 rounded-2xl p-3"
            style={{
              minWidth: 210,
              background:
                'linear-gradient(180deg, rgba(28,28,42,0.98) 0%, rgba(18,18,30,0.98) 100%)',
              border: '1px solid var(--glass-stroke-strong)',
              boxShadow: 'var(--glass-shadow)',
            }}
          >
            <p className="text-[9px] uppercase tracking-[0.18em] text-[var(--text-muted)] font-mono mb-2">
              {t('networkTree.legend.borderColor')}
            </p>
            <LegendRow color="hsl(50 90% 62%)" label={t('networkTree.legend.you')} />
            <LegendRow
              color="hsl(150 75% 55%)"
              label={t('networkTree.legend.activeSlot')}
            />
            <LegendRow color="hsl(0 0% 30%)" label={t('networkTree.legend.noActiveSlot')} />
            <LegendRow
              color="hsl(0 0% 28%)"
              label={t('networkTree.legend.lineLocked')}
            />

            <p className="text-[9px] uppercase tracking-[0.18em] text-[var(--text-muted)] font-mono mt-3 mb-2">
              {t('networkTree.legend.badges')}
            </p>
            <LegendText
              badge="⚡N"
              text={t('networkTree.legend.badgeN')}
            />
            <LegendText
              badge="R3"
              color="hsl(45 95% 60%)"
              text={t('networkTree.legend.badgeRank')}
            />
            <LegendText
              badge="S2"
              color="hsl(280 70% 70%)"
              text={t('networkTree.legend.badgeStatus')}
            />
            <LegendText badge="👑" text={t('networkTree.legend.badgeLeader')} />

            <p className="text-[9px] uppercase tracking-[0.18em] text-[var(--text-muted)] font-mono mt-3 mb-2">
              {t('networkTree.legend.rightButtons')}
            </p>
            <LegendIcon
              icon={<Filter size={11} strokeWidth={2.2} />}
              text={t('networkTree.legend.filterDesc')}
            />
            <LegendIcon
              icon={<Crosshair size={11} strokeWidth={2.2} />}
              text={t('networkTree.legend.centerDesc')}
            />
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

function LegendIcon({
  icon,
  text,
}: {
  icon: React.ReactNode;
  text: string;
}) {
  return (
    <div className="flex items-start gap-2 py-0.5">
      <span
        className="inline-flex items-center justify-center rounded-full flex-shrink-0"
        style={{
          width: 20,
          height: 20,
          background: 'var(--glass-fill)',
          border: '1px solid var(--glass-stroke)',
          color: 'var(--text-secondary)',
          marginTop: 1,
        }}
      >
        {icon}
      </span>
      <span className="text-[11px] text-[var(--text-muted)] leading-[1.35]">
        {text}
      </span>
    </div>
  );
}

function LegendRow({ color, label }: { color: string; label: string }) {
  return (
    <div className="flex items-center gap-2 py-1">
      <span
        style={{
          width: 14,
          height: 14,
          borderRadius: '50%',
          border: `2px solid ${color}`,
          background: '#0f0f18',
          flexShrink: 0,
        }}
      />
      <span className="text-[11px] text-[var(--text-secondary)]">{label}</span>
    </div>
  );
}

function LegendText({
  badge,
  text,
  color = 'var(--text-secondary)',
}: {
  badge: string;
  text: string;
  color?: string;
}) {
  return (
    <div className="flex items-center gap-2 py-0.5">
      <span
        className="inline-flex items-center justify-center rounded-md font-mono text-[10px] font-bold"
        style={{
          minWidth: 24,
          padding: '1px 5px',
          background: 'var(--glass-fill)',
          border: '1px solid var(--glass-stroke)',
          color,
        }}
      >
        {badge}
      </span>
      <span className="text-[11px] text-[var(--text-muted)]">{text}</span>
    </div>
  );
}

// -----------------------------------------------------------------------------
// Export: оборачиваем в ReactFlowProvider для useReactFlow hooks
// -----------------------------------------------------------------------------
export function NetworkTree() {
  return (
    <ReactFlowProvider>
      <NetworkTreeInner />
    </ReactFlowProvider>
  );
}
